<template>
  <div>
    <div class="flex -mx-1">
      <template v-for="index in 5" :key="index">
        <div class="w-1/5 px-1">
          <div
            class="h-1 rounded-lg transition-colors"
            :class="barClass(index)"
          />
        </div>
      </template>
    </div>
    <div v-if="suggestions || warning || pwned" class="text-sm mt-3 text-gray-600 dark:text-gray-300">
      <span v-if="pwned" v-html="$t('components.elements.passwordStrength.pwned')" />

      <template v-else>
        <template v-if="suggestions && !warning">
          <span>
            {{ suggestions.join('. ') }}
          </span>
        </template>

        <template v-if="warning">
          <span>
            {{ warning }}
          </span>
        </template>
      </template>
    </div>
  </div>
</template>

<script lang="ts">
export default { name: 'OPasswordStrength' }
</script>

<script setup lang="ts">
import zxcvbn from 'zxcvbn'
import debounce from 'lodash/debounce'
import axios from 'axios'
import sha1 from 'sha1'

interface Props {
  password?: string | number | null
}

const props = withDefaults(defineProps<Props>(), {
  password: null,
})

const emit = defineEmits<{
  (e: 'update:strength', value: number | null): void
}>()

const pwned = ref(false)

const suggestions = computed(() => {
  return props.password ? zxcvbn(String(props.password)).feedback?.suggestions : null
})

const warning = computed(() => {
  return props.password ? zxcvbn(String(props.password)).feedback?.warning : null
})

const strength = computed(() => {
  if (pwned.value) {
    return 1
  }
  return props.password ? zxcvbn(String(props.password)).score + 1 : null
})

const secure = computed(() => {
  return props.password ? String(props.password).length >= 7 : null
})

const active = computed(() => {
  return props.password && String(props.password).length > 0
})

watch(strength, () => {
  emit('update:strength', strength.value)
})

watch(() => props.password, () => {
  checkPwned()
})

function barClass(index: number) {
  if (index <= (strength.value || 0)) {
    if ((strength.value || 0) <= 2) {
      return 'bg-red-400'
    } else if ((strength.value || 0) <= 4) {
      return 'bg-yellow-400'
    } else {
      return 'bg-green-500'
    }
  } else {
    return 'bg-gray-300'
  }
}

const checkPwned = debounce(async function () {
  const password = String(props.password)
  const hash = sha1(password).toUpperCase()
  const hashPrefix = hash.substring(0, 5)

  try {
    const url = `https://api.pwnedpasswords.com/range/${hashPrefix}`
    const response = await axios.get(url, { headers: { Accept: 'application/json' } })

    if (response.status === 200) {
      const hashSuffixes = response.data.split('\n')

      for (const suffix of hashSuffixes) {
        const [suffixHash, count] = suffix.split(':')

        if (hashPrefix + suffixHash === hash) {
          pwned.value = true
          return
        }
      }
    }
  } catch (error) {
    console.error('Error making the API call:', error)
  }

  pwned.value = false
}, 1000)
</script>
